import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["label", "children", "render", "placement", "open", "onOpen", "onClose", "border", "shadow", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "contentRef", "mountNode", "defaultFocusElement", "liveRegion", "onDismiss", "onTransition", "shouldReturnFocus", "shouldCloseOnEscape", "shouldCloseOnDocumentClick", "shouldContainFocus", "styles"];
var _dec, _dec2, _dec3, _class, _DrawerTray;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { withStyle } from '@instructure/emotion';
import { textDirectionContextConsumer } from '@instructure/ui-i18n';
import { Transition } from '@instructure/ui-motion';
import { omitProps } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { testable } from '@instructure/ui-testable';
import { Dialog } from '@instructure/ui-dialog';
import { Portal } from '@instructure/ui-portal';
import { mirrorHorizontalPlacement } from '@instructure/ui-position';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { DrawerLayoutContext } from '../index';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
parent: DrawerLayout
id: DrawerLayout.Tray
---
**/
let DrawerTray = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = textDirectionContextConsumer(), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_DrawerTray = class DrawerTray extends Component {
  get _content() {
    return this.ref;
  }
  constructor(props) {
    super(props);
    this.ref = null;
    this._DOMNode = null;
    this.makeStyleProps = () => {
      return {
        placement: this.placement
      };
    };
    this.handleContentRef = node => {
      this.ref = node;
      if (typeof this.props.contentRef === 'function') {
        this.props.contentRef(node);
      }
    };
    this.handleTransitionEntered = _transitionType => {
      this.setState({
        transitioning: false
      });
    };
    this.handleTransitionExited = _transitionType => {
      this.setState({
        transitioning: false
      });
    };
    this.handlePortalOpen = DOMNode => {
      this.DOMNode = DOMNode;
      this.setState({
        portalOpen: true
      });
    };
    this.state = {
      transitioning: false,
      portalOpen: false
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps());
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    if (this.props.open !== prevProps.open) {
      this.setState({
        transitioning: true
      });
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps());
  }
  get placement() {
    const _this$props3 = this.props,
      placement = _this$props3.placement,
      dir = _this$props3.dir;
    const isRtl = dir === textDirectionContextConsumer.DIRECTION.rtl;
    return isRtl ? mirrorHorizontalPlacement(placement, ' ') : placement;
  }
  get transition() {
    return this.placement === 'end' ? 'fade' : 'slide-left';
  }
  get DOMNode() {
    return this._DOMNode;
  }
  set DOMNode(el) {
    this._DOMNode = el;
  }
  renderContent() {
    const _this$props4 = this.props,
      children = _this$props4.children,
      render = _this$props4.render;
    if (typeof render === 'function') {
      return render();
    } else if (typeof children === 'function') {
      return children();
    } else {
      return children;
    }
  }
  render() {
    const _this$props5 = this.props,
      label = _this$props5.label,
      children = _this$props5.children,
      render = _this$props5.render,
      placement = _this$props5.placement,
      open = _this$props5.open,
      onOpen = _this$props5.onOpen,
      onClose = _this$props5.onClose,
      border = _this$props5.border,
      shadow = _this$props5.shadow,
      onEnter = _this$props5.onEnter,
      onEntering = _this$props5.onEntering,
      onEntered = _this$props5.onEntered,
      onExit = _this$props5.onExit,
      onExiting = _this$props5.onExiting,
      onExited = _this$props5.onExited,
      contentRef = _this$props5.contentRef,
      mountNode = _this$props5.mountNode,
      defaultFocusElement = _this$props5.defaultFocusElement,
      liveRegion = _this$props5.liveRegion,
      onDismiss = _this$props5.onDismiss,
      onTransition = _this$props5.onTransition,
      shouldReturnFocus = _this$props5.shouldReturnFocus,
      shouldCloseOnEscape = _this$props5.shouldCloseOnEscape,
      shouldCloseOnDocumentClick = _this$props5.shouldCloseOnDocumentClick,
      shouldContainFocus = _this$props5.shouldContainFocus,
      styles = _this$props5.styles,
      props = _objectWithoutProperties(_this$props5, _excluded);
    return _jsx(DrawerLayoutContext.Consumer, {
      children: shouldOverlayTray => {
        var _this$props$styles;
        const portalOpen = this.state.portalOpen;
        const needsPortal = shouldOverlayTray && !!mountNode;
        // we have to handle the shadow version here,
        // because passing and handling it in styles.js makes it
        // rerender the component during the transition, breaking it
        const trayStyles = shadow && shouldOverlayTray ? styles === null || styles === void 0 ? void 0 : styles.drawerTrayWithShadow : styles === null || styles === void 0 ? void 0 : styles.drawerTray;
        let transitionIn = open;
        if (needsPortal && !portalOpen) {
          transitionIn = false;
        }
        const content = _jsx(Transition, {
          in: transitionIn,
          type: this.transition,
          onTransition: onTransition,
          onEnter: onEnter,
          onEntering: onEntering,
          onEntered: createChainedFunction(this.handleTransitionEntered, onEntered, onOpen),
          onExit: onExit,
          onExiting: onExiting,
          onExited: createChainedFunction(this.handleTransitionExited, onExited, onClose),
          unmountOnExit: true,
          children: _jsx("div", {
            ...omitProps(props, DrawerTray.allowedProps),
            ref: this.handleContentRef,
            css: trayStyles,
            children: _jsx(Dialog, {
              open: true,
              role: shouldOverlayTray ? 'dialog' : 'region',
              label: label,
              shouldReturnFocus: shouldReturnFocus,
              shouldContainFocus: !this.state.transitioning && shouldContainFocus && shouldOverlayTray,
              shouldCloseOnDocumentClick: shouldCloseOnDocumentClick && shouldOverlayTray,
              shouldCloseOnEscape: shouldCloseOnEscape && shouldOverlayTray,
              defaultFocusElement: defaultFocusElement,
              liveRegion: liveRegion,
              onDismiss: onDismiss,
              as: "div",
              css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.drawerTrayContent,
              children: this.renderContent()
            })
          })
        });
        if (needsPortal) {
          return _jsx(Portal, {
            mountNode: mountNode,
            open: true,
            onOpen: this.handlePortalOpen,
            children: content
          });
        } else {
          return content;
        }
      }
    });
  }
}, _DrawerTray.displayName = "DrawerTray", _DrawerTray.componentId = 'DrawerLayout.Tray', _DrawerTray.locatorAttribute = 'data-drawer-tray', _DrawerTray.propTypes = propTypes, _DrawerTray.allowedProps = allowedProps, _DrawerTray.defaultProps = {
  shouldContainFocus: true,
  shouldCloseOnEscape: true,
  shouldCloseOnDocumentClick: true,
  shouldReturnFocus: true,
  open: false,
  shadow: true,
  border: true,
  placement: 'start'
}, _DrawerTray)) || _class) || _class) || _class);
export default DrawerTray;
export { DrawerTray };